import { styled } from "@linaria/react";
import * as React from "react";
import { useModals } from "@ui/lib/hooks/useModal";
import t from "../../../../../i18n";
import Modal from "@ui/Modal";

import type { Project } from "../../../../../api/useProjects";
import type { Setup } from "../../../../../api/useSetups";
import {
  GlobalDiagnosisStatus,
  ExactDiagnosisCauses,
  useSetups,
} from "../../../../../api/useSetups";
import ProjectAlgorithm from "./Algorithm";
import { FailuresInfo } from "./Failures";
import { DeleteObservables } from "./Observables/DeleteModalForm";
import ImportModalForm from "./Observables/ImportModalForm";
import type { ObservableModal } from "./Observables";
import { ObservablesInfo } from "./Observables";
import { useQuantities } from "../../../../../api/useQuantities";
import { StatusDisplay, mapStatusDisplay } from "../StatusDisplay/utils";
import Banner from "../StatusDisplay/Banner";
import BannerTextList from "../StatusDisplay/BannerTextList";
import BannerTitle from "../StatusDisplay/BannerTitle";
import { H3 } from "@ui/Text";
import { css } from "@linaria/core";
import { LoadedFileName } from "../../LoadedFileName";
import { useOutletContext } from "react-router";

const Container = styled.div`
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const SectionTitle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const getStatusTitle = (statusType: GlobalDiagnosisStatus) => {
  switch (statusType) {
    case GlobalDiagnosisStatus.INCOMPLETE:
      return t("setup.diagnosis.status.incomplete");
    case GlobalDiagnosisStatus.INVALID:
      return t("setup.diagnosis.status.invalid");
    case GlobalDiagnosisStatus.COMPLETE:
      return t("setup.diagnosis.status.complete");
    default:
      throw new Error(statusType satisfies never);
  }
};

const diagnosisBannerStatus = mapStatusDisplay<GlobalDiagnosisStatus>([
  [GlobalDiagnosisStatus.INCOMPLETE, StatusDisplay.PENDING],
  [GlobalDiagnosisStatus.INVALID, StatusDisplay.ERROR],
  [GlobalDiagnosisStatus.COMPLETE, StatusDisplay.SUCCESS],
]);

export const ProjectSetupDiagnosis = () => {
  const { project, setup } = useOutletContext<{ project: Project; setup: Setup }>();
  if (!project) throw new Error("no project");
  if (!setup) throw new Error("no setup");

  const setups = useSetups(project.id);
  if (!setups) throw new Error("no setups");

  const { modal, closeModal, openModal } = useModals<ObservableModal>();
  const { id: setupId, name: setupName } = setup;
  const { id: projectId } = project;
  const quantities = useQuantities(projectId);

  const getQuantitiesNamesFromId = React.useMemo(
    () => (diagStatusQtyIds: string[]) =>
      diagStatusQtyIds.map(item => quantities.find(q => q.id === item)?.name || ""),
    [quantities],
  );

  const notSimulationOutputQtyIds = React.useMemo(
    () =>
      setup.diagnosisValidity.find(v => v.status === ExactDiagnosisCauses.NOT_SIMULATION_OUTPUT)
        ?.quantityIds,
    [setup],
  );

  const notSimulationOutputQtyNames = React.useMemo(
    () => getQuantitiesNamesFromId(notSimulationOutputQtyIds ?? []),
    [getQuantitiesNamesFromId, notSimulationOutputQtyIds],
  );

  const missingImpactQtyIds = React.useMemo(
    () =>
      setup.diagnosisValidity.find(v => v.status === ExactDiagnosisCauses.MISSING_IMPACT)
        ?.quantityIds,
    [setup],
  );

  const missingImpactQtyNames = React.useMemo(
    () => getQuantitiesNamesFromId(missingImpactQtyIds ?? []),
    [getQuantitiesNamesFromId, missingImpactQtyIds],
  );

  const getStatusContent = React.useCallback(
    (status: { status: ExactDiagnosisCauses }[]) => {
      const wording = status.map(s => {
        switch (s.status) {
          case ExactDiagnosisCauses.NO_INPUT:
            return t("setup.diagnosis.status.incomplete.noInput");
          case ExactDiagnosisCauses.NO_IMPACT:
            return t("setup.diagnosis.status.incomplete.noImpact");
          case ExactDiagnosisCauses.NO_FAILURE:
            return t("setup.diagnosis.status.incomplete.noFailure");
          case ExactDiagnosisCauses.NO_ENABLED_INPUT:
            return t("setup.diagnosis.status.incomplete.noEnabledInput");
          case ExactDiagnosisCauses.NO_ENABLED_FAILURE:
            return t("setup.diagnosis.status.incomplete.noEnabledFailure");
          case ExactDiagnosisCauses.NOT_SIMULATION_OUTPUT:
            return t(
              "setup.diagnosis.status.invalid.notSimulationOutput",
              notSimulationOutputQtyNames,
            );
          case ExactDiagnosisCauses.MISSING_IMPACT:
            return t("setup.diagnosis.status.invalid.missingImpact", missingImpactQtyNames);
          default:
            throw new Error(s.status satisfies never);
        }
      });

      return wording;
    },
    [missingImpactQtyNames, notSimulationOutputQtyNames],
  );

  const statusTitle = getStatusTitle(setup.diagnosisStatus);
  const statusContent = getStatusContent(setup.diagnosisValidity);

  return (
    <>
      <Banner status={diagnosisBannerStatus(setup.diagnosisStatus)}>
        <BannerTitle>{statusTitle}</BannerTitle>
        {statusContent.length > 0 && <BannerTextList explanations={statusContent} />}
      </Banner>
      <Container>
        <ProjectAlgorithm key={setupId} projectId={projectId} setup={setup} />
        <section>
          <div className={SectionTitle}>
            <H3>{t("setup.diagnosis.observable.title")}</H3>
            <LoadedFileName fileName={setup.observablesFileName} />
          </div>
          <ObservablesInfo openModal={openModal} setup={setup} projectId={projectId} />
        </section>
        <section>
          <div className={SectionTitle}>
            <H3>{t("setup.diagnosis.failure.title")}</H3>
            <LoadedFileName fileName={setup.faultsFileName} />
          </div>
          <FailuresInfo project={project} setups={setups} setup={setup} failures={setup.failures} />
        </section>
      </Container>

      <Modal
        title={t("setup.diagnosis.observable.import_observables.title")}
        isOpen={modal === "import"}
        onRequestClose={closeModal}
      >
        <ImportModalForm project={project} setup={setup} onRequestClose={closeModal} />
      </Modal>

      <Modal
        title={t("setup.diagnosis.observable.delete_observables.title")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        <DeleteObservables
          setupName={setupName}
          project={project}
          setup={setup}
          onRequestClose={closeModal}
        />
      </Modal>
    </>
  );
};
